import { Redo, Undo } from "@mui/icons-material";
import { Box } from "@mui/material";
import MDEditor, { commands, ICommand, MDEditorProps } from "@uiw/react-md-editor";
import { useState, useRef, useEffect } from "react";

type MarkdownEditorProps = {
    value: string;
    onChange: (value?: string) => void;
    undo: () => void;
    redo: () => void;
    customStyles: object;
} & MDEditorProps

export const MarkdownEditor = ({
    value,
    onChange,
    undo,
    redo,
    customStyles,
    ...props
}: MarkdownEditorProps) => {
    const [editorHeight, setEditorHeight] = useState<string>("auto");
    const previewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateHeight = () => {
            if (previewRef.current) {
                const previewElement = previewRef.current.querySelector('.wmde-markdown');
                if (previewElement) {
                    const previewHeight = previewElement.clientHeight;
                    setEditorHeight(`${Math.max(previewHeight + 120, 400)}px`);
                }
            }
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        if (previewRef.current) {
            observer.observe(previewRef.current);
        }

        return () => observer.disconnect();
    }, [value]);

    const extraCommands: ICommand[] = [
        {
            name: 'undo',
            keyCommand: 'undo',
            shortcuts: 'ctrl+z',
            icon: <Undo sx={{ fontSize: '12px' }} />,
            buttonProps: { 'aria-label': 'Undo', title: 'Undo (Ctrl+Z)' },
            execute: undo
        },
        {
            name: 'redo',
            keyCommand: 'redo',
            shortcuts: 'ctrl+shift+z',
            icon: <Redo sx={{ fontSize: '12px' }} />,
            buttonProps: { 'aria-label': 'Redo', title: 'Redo (Ctrl+Shift+Z)' },
            execute: redo
        },
    ];

    return (
        <Box
            ref={previewRef}
            sx={{
                ...customStyles,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
            }}
        >
            <MDEditor
                value={value}
                onChange={onChange}
                commands={[
                    ...extraCommands,
                    commands.divider,
                    ...commands.getCommands(),
                ]}
                preview="live"
                height={editorHeight}
                visibleDragbar={false}
                previewOptions={{
                    ...props.previewOptions,
                    className: 'editor-preview'
                }}
                {...props}
            />
        </Box>
    );
}; 